import { useCallback, useEffect, useMemo, useState } from 'react'

import { login as requestLogin } from '../lib/api'
import { SESSION_KEY, clearSession, loadSession, saveSession } from '../lib/session'
import { AuthContext } from './authContext'

// setTimeout wraps around past ~24.8 days; no token here lives anywhere near that.
const MAX_TIMEOUT_MS = 2 ** 31 - 1

export default function AuthProvider({ children }) {
  const [session, setSession] = useState(loadSession)
  const [expired, setExpired] = useState(false)

  const login = useCallback(async (credentials) => {
    // Throws a LoginError for every failure, so the caller only has to catch.
    const result = await requestLogin(credentials)
    const next = saveSession(result)
    setSession(next)
    setExpired(false)
    return next
  }, [])

  const logout = useCallback(() => {
    clearSession()
    setSession(null)
    setExpired(false)
  }, [])

  // Sign out the moment the token runs out, rather than leaving the page up
  // until something else happens to notice.
  useEffect(() => {
    if (!session) return undefined
    const wait = Math.min(Math.max(0, session.expiresAt - Date.now()), MAX_TIMEOUT_MS)
    const timer = setTimeout(() => {
      clearSession()
      setSession(null)
      setExpired(true)
    }, wait)
    return () => clearTimeout(timer)
  }, [session])

  // Signing out in one tab signs out the others.
  useEffect(() => {
    const onStorage = (event) => {
      if (event.key === SESSION_KEY && !event.newValue) setSession(null)
    }
    window.addEventListener('storage', onStorage)
    return () => window.removeEventListener('storage', onStorage)
  }, [])

  const value = useMemo(() => ({ session, expired, login, logout }), [session, expired, login, logout])

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}
